const ignoredWords = new Set(['a', 'an', 'and', 'at', 'in', 'of', 'on', 'the', 'to']);

export const normalizeEntityKey = (value = '') => String(value)
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/(^-|-$)/g, '');

export const eventEntityRecordId = (item = {}) => item.entityId
  || item.id
  || [normalizeEntityKey(item.name || item.title), item.year].filter(Boolean).join('-');

const mediaText = (item = {}) => normalizeEntityKey([
  item.id, item.title, item.name, item.heading, item.alt, item.caption,
].filter(Boolean).join(' '));

const identityKeys = (item = {}, entity = {}) => {
  const resolvedEntity = entity || {};
  return [
    item.entityId,
    item.id,
    item.name,
    item.title,
    item.titleOverride,
    resolvedEntity.id,
    resolvedEntity.name,
  ].filter(Boolean).map(normalizeEntityKey);
};

const matchesIdentity = (asset, item, entity) => {
  const haystack = mediaText(asset);
  return identityKeys(item, entity).some((identity) => {
    if (haystack.includes(identity) || identity.includes(haystack)) return true;
    const tokens = identity.split('-').filter((token) => token.length > 2 && !ignoredWords.has(token));
    return tokens.length > 0 && tokens.every((token) => haystack.includes(token));
  });
};

const normalizeMedia = (asset, index, recordId) => {
  if (!asset) return null;
  if (typeof asset === 'string') return { id: `${recordId}-media-${index + 1}`, src: asset, alt: '' };
  const src = asset.src || asset.image?.src || asset.image;
  if (!src && !asset.firebaseAssetId) return null;
  return {
    ...asset,
    id: asset.id || `${recordId}-media-${index + 1}`,
    ...(src ? { src } : {}),
    alt: asset.alt || asset.imageAlt || '',
  };
};

const directMedia = (item, entity, recordId) => [
  ...(item.images || []),
  ...(item.videos || []).map((video) => ({ ...video, type: 'video' })),
  item.cardImage,
  item.image ? { src: item.image, alt: item.imageAlt, caption: item.imageCaption } : null,
  entity?.heroImage,
].filter(Boolean).map((asset, index) => normalizeMedia(asset, index, recordId)).filter(Boolean);

const uniqueMedia = (media) => {
  const seen = new Set();
  return media.filter((asset) => {
    const identity = asset?.firebaseAssetId || asset?.src?.firebaseAssetId || asset?.src;
    if (!identity || seen.has(identity)) return false;
    seen.add(identity);
    return true;
  });
};

export const eventSectionItems = (event, sectionKey) => {
  const source = event || {};
  if (sectionKey === 'dining') {
    return source.restaurants?.length ? source.restaurants : (source.restaurantVisits || []);
  }
  return source.activities || [];
};

export const resolveEventEntityDetails = ({
  event, sectionKey, item, entity,
}) => {
  if (!event || !item) return null;
  const recordId = eventEntityRecordId(item);
  const taggedMedia = (event.gallery || [])
    .filter(Boolean)
    .filter((asset) => matchesIdentity(asset, item, entity))
    .map((asset, index) => normalizeMedia(asset, index, recordId))
    .filter(Boolean);
  const media = uniqueMedia([...taggedMedia, ...directMedia(item, entity, recordId)]);
  return {
    id: recordId,
    sectionKey,
    title: item.titleOverride || item.name || item.title || entity?.name || 'Experience',
    description: item.eventDescription || item.description || entity?.shortDescription || '',
    dayLabel: item.dayLabel || (item.year ? String(item.year) : ''),
    location: item.location || entity?.location || '',
    websiteUrl: item.websiteUrl || entity?.websiteUrl || '',
    mapUrl: item.mapUrl || entity?.mapUrl || '',
    storyItems: [
      ['Why we went', item.whyWeWent],
      ['What happened', item.whatHappened],
      ['What we remember', item.whatWeRemember],
      ['Our take', item.recommendation],
    ].filter(([, value]) => value).map(([label, value]) => ({ label, value })),
    storySections: item.storySections || [],
    storyPages: item.storyPages || [],
    media,
  };
};

export const eventEntityRoute = (event, sectionKey, item, storyPageSlug = '') => {
  const params = {
    gallerySlug: event.slug,
    sectionKey,
    entityId: eventEntityRecordId(item),
  };
  const basePageSlug = item.storyPages?.[0]?.slug;
  if (storyPageSlug && storyPageSlug !== basePageSlug) {
    return {
      name: 'reckon-way-entity-story-page',
      params: { ...params, storyPageSlug },
    };
  }
  return { name: 'reckon-way-entity', params };
};
